import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const CONTAINER = 'max-w-6xl mx-auto px-6 lg:px-8';
const H1 = 'text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-500 leading-relaxed';

const STEPS = [
  {
    num: '01',
    title: 'Post a shift',
    kicker: 'Under a minute.',
    body: 'Choose a branch, a date, hours and pay rate. Add any specific requirements — till experience, retail background, weekend availability — and hit post. Your shift is instantly visible to verified workers matching your criteria.',
    bullets: ['Branch selector with saved locations', 'Rate suggestions from local averages', 'Optional requirements filter'],
  },
  {
    num: '02',
    title: 'Review applicants',
    kicker: 'Trust made visible.',
    body: 'Each applicant appears with their verified profile, reliability score, previous shifts and reviews from other managers. Filter, sort or message them directly to confirm the details before you decide.',
    bullets: ['Reliability history and no-show record', 'Verified identity and Smart ID checks', 'In-app messaging with each applicant'],
  },
  {
    num: '03',
    title: 'Confirm cover',
    kicker: 'Peace of mind.',
    body: 'Pick the right worker with one tap. They get a confirmation, arrival directions and reminders. Once on-shift, timestamped till checks and shift records give you a full audit trail — no messy paperwork.',
    bullets: ['Automatic worker confirmation & reminders', 'Timestamped till checks every 3 hours', 'Shift record ready for HR / payroll'],
  },
];

export default function HowItWorksPage() {
  return (
    <div data-testid="how-it-works-page">
      <section className={`${CONTAINER} pt-32 pb-16 lg:pt-40 lg:pb-20`}>
        <div className="max-w-3xl">
          <p className={EYEBROW}>How it works</p>
          <h1 className={`${H1} mt-5`}>Three steps to trusted cover.</h1>
          <p className={`${BODY} mt-6 max-w-xl`}>
            Post a shift, review verified workers, confirm cover. Simple, transparent, and built for customer-facing businesses.
          </p>
        </div>
      </section>

      <section className={`${CONTAINER} pb-24 lg:pb-32 space-y-24 lg:space-y-32`}>
        {STEPS.map((s, i) => (
          <div key={s.num} className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-center" data-testid={`step-detail-${s.num}`}>
            <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
              <p className="text-7xl md:text-8xl font-bold text-slate-200 tracking-tighter tabular-nums">{s.num}</p>
              <p className={`${EYEBROW} mt-6`}>{s.kicker}</p>
              <h2 className={`${H2} mt-3`}>{s.title}</h2>
              <p className={`${BODY} mt-5 max-w-md`}>{s.body}</p>
              <ul className="mt-6 space-y-2.5">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-[14.5px] text-slate-700">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#DA291C] shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
              <div className="aspect-[4/3] rounded-3xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200/70 flex items-center justify-center overflow-hidden shadow-[0_16px_40px_rgba(11,19,43,0.06)]">
                <div className="text-center px-8">
                  <p className="text-8xl font-bold text-slate-200 tabular-nums">{s.num}</p>
                  <p className="mt-4 text-sm font-semibold text-slate-500 uppercase tracking-widest">{s.title}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-slate-50/70 py-20 lg:py-24">
        <div className={`${CONTAINER} text-center max-w-2xl`}>
          <h2 className={`${H2}`}>Ready to try it?</h2>
          <p className={`${BODY} mt-4`}>14-day free trial. No card required.</p>
          <Link to="/register?role=manager" className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white font-semibold hover:bg-[#b91f13] transition-colors" data-testid="how-it-works-cta">
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
